import { AfterViewInit, Component, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';

// translation service
import { TranslatePipe } from '@ngx-translate/core'; 


// angular material
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';

//models
import { Client } from '../../models/client';
import { IContactEventConfiguration, User } from '../../models/user';
// services
import { UserService } from '../../services/user-service';
import { CaseloadTableColumns, ContactEventTableColumns } from '../../services/common-service';
import { CaseloadConfiguration } from '../../models/user';



// components
import { PagedDatatableComponent } from '../../utils/paged-datatable-component/paged-datatable-component';

@Component( {
  selector: 'app-caseload',
  providers: [provideNativeDateAdapter()],
  imports: [
    RouterLink,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatMenuModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatToolbarModule,
    MatBadgeModule,
    MatTabsModule,
    MatDividerModule,
    MatExpansionModule,
    MatTableModule,
    PagedDatatableComponent,
    MatDatepickerModule,
    MatCheckboxModule,
    TranslatePipe,
  ],
  templateUrl: './caseload.html',
  styleUrl: './caseload.css',
  changeDetection: ChangeDetectionStrategy.OnPush
} )
export class Caseload implements AfterViewInit
{
  @ViewChild( 'clients' ) clientsPagedDataTable!: PagedDatatableComponent;
  @ViewChild( 'contacts' ) contactsPagedDataTable!: PagedDatatableComponent;

  constructor(
    private _user: UserService,
    private _cdr: ChangeDetectorRef
  )
  {
    this.currentUser = this._user.getUser();
    console.log( 'Current User:', this.currentUser );
  }

  protected currentUser: User| null = null;
  protected viewedClients: Client[] = [];
  public clientsColumns = CaseloadTableColumns['Default'].DefaultColumns;
  public contactsColumns = ContactEventTableColumns.DefaultColumns;
  public clientCaseloadConfig: CaseloadConfiguration = {
    pagination: { pageNumber: 1, pageSize: 10, orderby: "LastName" },
    filters: {
      firstName: '', lastName: '', dateOfBirth: undefined, phone: '', shouldIncludeActive: true, shouldIncludeInactive: false,
      clientId: '', externalClientId: ''
    },
  };
  public contactFilterOpened: boolean = false;
  public caseloadFilterOpened: boolean = false;
  public viewedClientsOpened: boolean = false;
  public contactEventConfig: IContactEventConfiguration = {
    pagination: { pageNumber: 1, pageSize: 10, orderby: "LastName" }, filters: {
      dateFrom: undefined, dateTo: undefined, hubId: '', agencyId: undefined, clientId: '',
      searchBy: '', searchByValue: '', showHistoric: false, includeDeletedEvents: false,
      chwUserName: '', contactEventId: '', createdByUserName:''
    }
  };

  public async ngAfterViewInit(): Promise<void>
  {
    this.viewedClients = await this._user.getViewedClients();
    await this.ApplyClientFilters();
    await this.ApplyContactsFilters();   
    this._cdr.detectChanges();
  }

  public async loadClientsData( event: any ): Promise<void>
  {
    this.clientCaseloadConfig.pagination.pageNumber = event.pageIndex + 1;
    this.clientCaseloadConfig.pagination.pageSize = event.pageSize;
    const data = await this._user.getCaseload( this.clientCaseloadConfig );
    if ( data )
    {
      const pagination = { pageSizeOptions: [10, 20, 50, 100], pageIndex: event.pageIndex, length: data.total };
      this.clientsPagedDataTable.loadData( data, pagination );
      this._cdr.detectChanges();
    }    
  }

  public async loadContactsData( event: any ): Promise<void>
  {
    this.contactEventConfig.pagination.pageNumber = event.pageIndex + 1;
    this.contactEventConfig.pagination.pageSize = event.pageSize;
    this.contactEventConfig.filters!.hubId = this.currentUser?.hubId || '';
    const data = await this._user.getContactEvents( this.contactEventConfig );
    if ( data )
    {
      const pagination = { pageSizeOptions: [10, 20, 50, 100], pageIndex: event.pageIndex, length: data.total };
      this.contactsPagedDataTable.loadData( data, pagination );
      this._cdr.detectChanges();
    }    
  }

  public async ApplyClientFilters()
  {
    this.clientCaseloadConfig.pagination.pageNumber = 1;
    const data = await this._user.getCaseload( this.clientCaseloadConfig );
    if ( data )
    {
      const pagination = { pageSizeOptions: [10, 20, 50, 100], pageIndex: 0, length: data.total };
      this.clientsPagedDataTable.loadData( data, pagination );
      this._cdr.detectChanges();
    }
  }

  public async ApplyContactsFilters()
  {
    this.contactEventConfig.pagination.pageNumber = 1;
    const data = await this._user.getContactEvents( this.contactEventConfig );
    if ( data )
    {
      const pagination = { pageSizeOptions: [10, 20, 50, 100], pageIndex: 0, length: data.total };
      this.contactsPagedDataTable.loadData( data, pagination );
      this._cdr.detectChanges();
    }
  }

  public async sortClientsData( sort: any ): Promise<void>
  {    
    if ( !sort.active ) return;
    this.clientCaseloadConfig.pagination.orderby = `${sort.active} ${sort.direction}`;
    const data = await this._user.getCaseload( this.clientCaseloadConfig );
    if ( data )
    {
      const pagination = { pageSizeOptions: [10, 20, 50, 100], pageIndex: 0, length: data.total };
      this.clientsPagedDataTable.loadData( data, pagination );
      this._cdr.detectChanges();
    }    
  }

  public async sortContactsData( sort: any ): Promise<void>
  {    
    if ( !sort.active ) return;
    this.contactEventConfig.pagination.orderby = `${sort.active} ${sort.direction}`;
    const data = await this._user.getContactEvents( this.contactEventConfig );
    if ( data )
    {
      const pagination = { pageSizeOptions: [10, 20, 50, 100], pageIndex: 0, length: data.total };
      this.contactsPagedDataTable.loadData( data, pagination );
      this._cdr.detectChanges();
    }    
  }

  public async toggleContactFilter()
  {
    this.contactFilterOpened = !this.contactFilterOpened;
  }

  public async toggleCaseloadFilter()
  {
    this.caseloadFilterOpened = !this.caseloadFilterOpened;
  }

  public async toggleViewedClients()
  {
    this.viewedClientsOpened = !this.viewedClientsOpened;
  }
}

